import cx from 'classnames';
import { UserAvatar } from 'Components/shared/UserAvatar';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Dropdown } from 'semantic-ui-react';
import { PhoneCallModel } from '../../models';
import { TransferDirectoryListItemProps } from './interfaces';

const BLIND_TRANSFER = 'Blind Transfer';
const WARM_TRANSFER = 'Warm Transfer';
const VOICEMAIL_TRANSFER = 'Transfer to Voicemail';

interface ITransferCallDropdownProps {
  markup: any;
  number: number | string;
  phoneCall: PhoneCallModel;
  vmDisabled?: boolean;
}

export class TransferCallDropdown extends React.Component<
  ITransferCallDropdownProps,
  {}
> {
  handleChange = (e, { value }) => {
    e.persist();

    const { phoneCall } = this.props;
    const transferType = e.target.innerText;
    switch (transferType) {
      case BLIND_TRANSFER:
        phoneCall.blindTransfer(value);
        break;
      case WARM_TRANSFER:
        phoneCall.warmTransfer(value);
        break;
      case VOICEMAIL_TRANSFER:
        phoneCall.forwardToVoiceMail(value);
      default:
        break;
    }
  };

  render() {
    const { markup, number, vmDisabled } = this.props;
    const options = [
      { key: 1, text: BLIND_TRANSFER, value: number },
      { key: 2, text: WARM_TRANSFER, value: number, disabled: false },
      { key: 3, text: VOICEMAIL_TRANSFER, value: number, disabled: vmDisabled },
    ];

    return (
      <Dropdown
        trigger={markup}
        className="transfer-call-dropdown"
        options={options}
        icon={null}
        onChange={this.handleChange}
      />
    );
  }
}

export class TransferDirectoryListItem extends React.Component<
  TransferDirectoryListItemProps,
  { hovered: boolean }
> {
  onMouseOver = () => this.setState({ hovered: true });
  onMouseLeave = () => this.setState({ hovered: false });
  state = {
    hovered: false,
  };

  render() {
    const { hovered } = this.state;
    const {
      contact,
      conversationId,
      phoneCall,
      formatNumber,
      selectPersonPresenceStatus,
      unknownUser,
      unknownUserNumber,
    } = this.props;

    if (unknownUser && !isEmpty(unknownUserNumber)) {
      const unknownUserMarkup = (
        <div className="number">{formatNumber(unknownUserNumber)}</div>
      );

      return (
        <li
          key={'unknown-user'}
          className={cx(
            'list-item-ih semantic-flex-row directory-list-item',
            { hovered },
            'pointer'
          )}
          onMouseOver={this.onMouseOver}
          onMouseLeave={this.onMouseLeave}
        >
          <UserAvatar
            conversationGrouping="OneOnOne"
            conversationId={conversationId}
            presence={null}
          />
          {/* Unsaved Contacts */}
          <div className="content list-item-ih-content semantic-flex-fluid">
            <div className="header">Unknown User</div>
            <TransferCallDropdown
              markup={unknownUserMarkup}
              phoneCall={phoneCall}
              number={unknownUserNumber}
              vmDisabled={true}
            />
          </div>
        </li>
      );
    }

    const src = contact.source;
    const isVMDisabled = src.voicemailInboxId === '0';

    const isPerson = src.source === 'Bhive';
    const presenceStatus = isPerson
      ? selectPersonPresenceStatus(src.sourceId)
      : null;

    const officeNumMarkup =
      isPerson && (src.extensionNumber || src.phoneNumber) ? (
        <div className="number">
          OFFICE: {src.extensionNumber || src.phoneNumber}
        </div>
      ) : undefined;

    // Primary phone number is already shown in the OFFICE section
    const phoneNumbers = src.phoneNumbers.filter(
      (phoneNumber) => !phoneNumber.primary && !isEmpty(phoneNumber.number)
    );

    return (
      <li
        key={contact.source.id}
        className={cx(
          'list-item-ih semantic-flex-row directory-list-item',
          { hovered },
          'pointer'
        )}
        onMouseOver={this.onMouseOver}
        onMouseLeave={this.onMouseLeave}
      >
        <UserAvatar
          name={src.name}
          conversationId={conversationId}
          presence={presenceStatus}
        />
        <div className="content list-item-ih-content semantic-flex-fluid">
          <div className="header">{src.name}</div>

          {officeNumMarkup && (
            <TransferCallDropdown
              markup={officeNumMarkup}
              phoneCall={phoneCall}
              number={isPerson ? `pr${src.sourceId}` : src.phoneNumber}
              vmDisabled={isPerson ? isVMDisabled : true}
            />
          )}

          {phoneNumbers.map((phoneNumber, index) => (
            <TransferCallDropdown
              markup={
                <div className="number">
                  {phoneNumber.type.toUpperCase()}:{' '}
                  {formatNumber(phoneNumber.number)}
                </div>
              }
              phoneCall={phoneCall}
              number={phoneNumber.number}
              vmDisabled={true}
              // The phone number would be a better key, but they may be repeated, so we can't do that
              key={index}
            />
          ))}
        </div>
      </li>
    );
  }
}

export default observer(TransferDirectoryListItem);
